import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Obituary from './Obituary';
import Gallery from './Gallery';
import Timeline from './Timeline';
import MemoryWall from './MemoryWall';
import Condolences from './Condolences';
import Footer from './Footer';
import JohnSmith from '../images/JohnSmith.jpg';

type Tab = {
  id: string;
  label: string;
  requiresAuth?: boolean;
};

const MainLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<string>('obituary');

  const tabs: Tab[] = [
    { id: 'obituary', label: 'Life Story' },
    { id: 'gallery', label: 'Gallery' },
    { id: 'timeline', label: 'Timeline' },
    { id: 'memory-wall', label: 'Memories' }
  ];

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
    const mainContent = document.querySelector('main');
    if (mainContent) {
      const navHeight = document.querySelector('nav')?.offsetHeight || 0;
      const offset = mainContent.offsetTop - navHeight;
      window.scrollTo({
        top: offset,
        behavior: 'smooth'
      });
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'obituary':
        return <Obituary />;
      case 'gallery':
        return <Gallery />;
      case 'timeline':
        return <Timeline />;
      case 'memory-wall':
        return <MemoryWall />;
      default:
        return <Obituary />;
    }
  };

  return (
    <div className="min-h-screen bg-parchment-50">
      {/* Hero Header */}
      <header className="relative bg-[#1F0A0F] text-parchment-50 pt-16 pb-24">
        {/* Ornamental border top */}
        <div className="absolute top-0 left-0 right-0 h-2 bg-gold-400/90"></div>
        <div className="absolute top-2 left-0 right-0 h-[1px] bg-gold-200/30"></div>

        <div className="section-container [background-image:none]">
          {/* User profile area */}
          {user && (
            <div className="absolute top-4 right-4 md:top-6 md:right-6 z-10">
              <div className="flex items-center gap-2 md:gap-3 px-4 py-2 bg-[#1F0A0F] rounded-lg border border-gold-200/20">
                <span className="text-sm text-parchment-50 font-serif truncate max-w-[120px] md:max-w-none">
                  {user.name}
                </span>
                <div className="h-4 w-px bg-gold-200/20"></div>
                <button
                  onClick={logout}
                  className="text-sm text-parchment-50/90 hover:text-gold-300 transition-colors duration-300"
                >
                  Sign out
                </button>
              </div>
            </div>
          )}

          {/* Main content */}
          <div className="flex flex-col items-center space-y-28">
            {/* Portrait */}
            <div className="relative w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96">
              {/* Traditional ornate frame */}
              <div className="absolute -inset-10">
                {/* Main frame border */}
                <div className="absolute inset-0 rounded-lg border-2 border-gold-400/15">
                  {/* Inner frame accent */}
                  <div className="absolute inset-2 rounded-lg border border-gold-400/10"></div>
                  
                  {/* Decorative corners - outer */}
                  <div className="absolute -top-4 -left-4 w-16 h-16 border-t-2 border-l-2 border-gold-400/20 rounded-tl-xl"></div>
                  <div className="absolute -top-4 -right-4 w-16 h-16 border-t-2 border-r-2 border-gold-400/20 rounded-tr-xl"></div>
                  <div className="absolute -bottom-4 -left-4 w-16 h-16 border-b-2 border-l-2 border-gold-400/20 rounded-bl-xl"></div>
                  <div className="absolute -bottom-4 -right-4 w-16 h-16 border-b-2 border-r-2 border-gold-400/20 rounded-br-xl"></div>
                  
                  {/* Decorative corners - inner */}
                  <div className="absolute -top-2 -left-2 w-12 h-12 border-t border-l border-gold-400/25 rounded-tl-lg"></div>
                  <div className="absolute -top-2 -right-2 w-12 h-12 border-t border-r border-gold-400/25 rounded-tr-lg"></div>
                  <div className="absolute -bottom-2 -left-2 w-12 h-12 border-b border-l border-gold-400/25 rounded-bl-lg"></div>
                  <div className="absolute -bottom-2 -right-2 w-12 h-12 border-b border-r border-gold-400/25 rounded-br-lg"></div>
                </div>
              </div>
              
              {/* Image container */}
              <div className="relative w-full h-full overflow-hidden rounded-lg">
                {/* Enhanced vignette effect */}
                <div className="absolute inset-0 shadow-[inset_0_0_80px_rgba(0,0,0,0.45)]"></div>
                <img
                  src={JohnSmith}
                  alt="John David Smith"
                  className="w-full h-full object-cover transition-all duration-700 hover:scale-105"
                />
              </div>
            </div>

            {/* Text content */}
            <div className="text-center space-y-8 max-w-4xl">
              {/* Name and dates */}
              <div className="space-y-4">
                <div className="inline-block">
                  <h1 className="font-ornate text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-light tracking-tight text-parchment-50">
                    John David Smith
                  </h1>
                  <div className="mt-2 w-full h-px bg-gold-400/50"></div>
                </div>
                <p className="font-ornate text-xl sm:text-2xl md:text-3xl text-parchment-50 tracking-wide">
                  1950 — 2024
                </p>
              </div>

              {/* Decorative divider - single cross */}
              <div className="flex items-center justify-center gap-4 px-4">
                <div className="w-24 h-px bg-gold-400/30"></div>
                <div className="text-gold-400 text-2xl">✟</div>
                <div className="w-24 h-px bg-gold-400/30"></div>
              </div>

              {/* Quote */}
              <div className="relative max-w-3xl mx-auto px-8 pt-6">
                <div className="absolute -top-1 -left-2 text-4xl text-gold-400/30 font-serif">"</div>
                <div className="absolute -top-1 -right-2 text-4xl text-gold-400/30 font-serif">"</div>
                <p className="font-serif text-lg sm:text-xl md:text-2xl font-light leading-relaxed text-parchment-50 italic">
                  A beautiful soul who touched countless lives with kindness, wisdom, and love.
                  Their legacy lives on in the hearts of all who knew them.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Ornamental border bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gold-200/30"></div>
        <div className="absolute bottom-[1px] left-0 right-0 h-2 bg-gold-400/90"></div>
      </header>

      {/* Navigation - simplified decorative elements */}
      <nav className="sticky top-0 z-10 bg-[#1F0A0F]/98 backdrop-blur-md border-y border-gold-400/30 shadow-md overflow-x-auto">
        <div className="section-container py-0">
          {/* Decorative top border */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gold-200/20"></div>
          
          <div className="flex justify-center min-w-full">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className={`relative px-6 sm:px-10 py-4 text-base sm:text-lg font-serif tracking-wide transition-all duration-300 
                  ${activeTab === tab.id
                    ? 'text-gold-400'
                    : 'text-parchment-300/90'
                  }
                `}
              >
                {/* Decorative elements */}
                {activeTab === tab.id && (
                  <>
                    <span className="absolute top-1/2 -left-1 -translate-y-1/2 text-gold-400/30 text-sm">❦</span>
                    <span className="absolute top-1/2 -right-1 -translate-y-1/2 text-gold-400/30 text-sm transform rotate-180">❦</span>
                  </>
                )}
                
                <span className="relative z-10">{tab.label}</span>
                {activeTab === tab.id && (
                  <div className="absolute inset-0 bg-burgundy-900/30 rounded-sm"></div>
                )}
              </button>
            ))}
          </div>
          
          {/* Decorative bottom border */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gold-200/20"></div>
        </div>
      </nav>

      {/* Main content */}
      <main className="section-container relative px-4 sm:px-6 bg-parchment-50">
        <div className="relative space-y-48">
          <div className="animate-fade-in">
            {renderContent()}
          </div>
          <div className="animate-fade-in pt-24">
            <Condolences />
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default MainLayout; 